<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';
  import { useResizeObserver } from '@vueuse/core';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import { reservationTabs } from '@/domain/reservations/reservation-tabs';

  const props = defineProps<{
    value: string;
    canManage: boolean;
    canSettings: boolean;
    waitingCount: number;
    pendingCount: number;
  }>();
  const emit = defineEmits<{ (event: 'input', tab: string): void }>();
  const root = ref<HTMLElement | null>(null);
  const track = ref<HTMLElement | null>(null);
  async function revealActiveTab() {
    await nextTick();
    root.value
      ?.querySelector<HTMLElement>('[aria-selected="true"]')
      ?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }
  useResizeObserver([root, track], revealActiveTab);
  watch([() => props.value, root], revealActiveTab, { immediate: true });
  const tabs = computed(() => reservationTabs(props));

  function navigate(event: KeyboardEvent, index: number) {
    const next =
      event.key === 'ArrowRight'
        ? (index + 1) % tabs.value.length
        : event.key === 'ArrowLeft'
        ? (index + tabs.value.length - 1) % tabs.value.length
        : event.key === 'Home'
        ? 0
        : event.key === 'End'
        ? tabs.value.length - 1
        : -1;
    if (next < 0) return;
    event.preventDefault();
    emit('input', tabs.value[next].id);
    const target = event.currentTarget;
    if (target instanceof HTMLElement)
      target.parentElement
        ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
        [next]?.focus();
  }
</script>

<template>
  <nav ref="root" class="reservations-tabs" :aria-label="$t('reservations')">
    <PosUiScrollable
      orientation="horizontal"
      hide-scrollbar="touch"
      arrows
      theme="elevated"
    >
      <div ref="track" class="tabs-track" role="tablist">
        <button
          v-for="(tab, index) in tabs"
          :id="`reservations-tab-${tab.id}`"
          :key="tab.id"
          type="button"
          class="tab"
          :class="{ active: value === tab.id }"
          role="tab"
          :aria-label="$t(`reservations.tabs.${tab.id}`)"
          :aria-selected="value === tab.id"
          :aria-controls="`reservations-pane-${tab.id}`"
          :tabindex="value === tab.id ? 0 : -1"
          @click="emit('input', tab.id)"
          @keydown="navigate($event, index)"
        >
          <PosUiIcon :name="tab.icon" :size="18" aria-hidden="true" />
          <span>{{ $t(`reservations.tabs.${tab.id}`) }}</span>
          <PosUiBadge
            v-if="tab.badge"
            :label="tab.badge.count"
            :title="tab.badge.titleKey ? $t(tab.badge.titleKey) : undefined"
            :color="tab.badge.color"
            size="xs"
          />
        </button>
      </div>
    </PosUiScrollable>
  </nav>
</template>

<style scoped lang="scss">
  .reservations-tabs {
    @apply tw-min-w-0 tw-max-w-full;
    .tabs-track {
      @apply tw-inline-flex tw-rounded-xl tw-bg-neutral-100 tw-p-1;
    }
    .tab {
      @apply tw-flex tw-min-h-11 tw-items-center tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-500;
      > * + * {
        @apply tw-ml-2;
      }
      &.active {
        @apply tw-bg-white tw-text-neutral-900 tw-shadow-sm;
      }
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
